import {Column} from "primereact/column";
import {ColumnGroup} from "primereact/columngroup";
import {DataTable} from "primereact/datatable";
import {InputNumber} from "primereact/inputnumber";
import {Row} from "primereact/row";
import {Tooltip} from "primereact/tooltip";
import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared/i18n";
import {SEGMENT} from "@reva/shared/reva-spec";

import {useMultipleParameterValues, useParameterValue} from "../../component/paramInput/paramHooks";
import ResetIcon from "../../component/paramInput/ResetIcon";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectQualityFurnishingsPreview, setQualityFurnishingsPreview} from "../../redux/objectInformationSlice";
import {abortQualityFurnishingsQuery, queryQualityFurnishingsAsync} from "../../redux/objectInformationThunk";
import {
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
    selectSegment,
} from "../../redux/objectParametersSlice";
import {selectPersistenceInitialized, selectReadOnly} from "../../redux/persistenceSlice";
import {useRevaStore} from "../../redux/revaHooks";
import {selectQualityFurnishingsCraftToMatrixKeyCurried} from "../../redux/specSelectors";
import {selectSpec} from "../../redux/specSlice";

const OUTPUT_PARAMS = ["nhk_crafts_matrix", "nhk_crafts_based_furnishings"];
const RESULT_PARAMS = [...OUTPUT_PARAMS, "quality_furnishings_continuous"];

const CRAFT_NAME_PREFIX_DE = "Stufengewichte für ";
const CRAFT_NAME_PREFIX_EN = "Weights for ";

export function craftLabel(paramDef, language) {
    const prefix = language === "en" ? CRAFT_NAME_PREFIX_EN : CRAFT_NAME_PREFIX_DE;
    const name = selectByLanguage(language, paramDef?.nameDe, paramDef?.nameEn)?.replace(prefix, "");
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : name;
}

/**
 * Faltet Stufen unterhalb der ersten für dieses Segment angebotenen Stufe (z.B. 1-2 bei MFHWGH_K,
 * firstField=2) dort hinein, statt sie als für den Nutzer unsichtbare Position zu belassen (#19405).
 * Für Segmente ohne ausgeblendete Stufen (firstField=0) ein No-Op.
 */
export function foldQualityFurnishingsWeights(weights, firstField) {
    const rawFoldedValue = weights.slice(0, firstField + 1).reduce((sum, v) => sum + (v || 0), 0);
    // gerundet, da die Aufsummierung sonst Floating-Point-Rauschen produziert und persistiert (z.B. 0.3 + 0.1 + 0.6 = 0.9999999999999999)
    const foldedValue = Math.round(rawFoldedValue * 10000) / 10000;
    return weights.map((v, i) => (i < firstField ? 0 : i === firstField ? foldedValue : v));
}

/**
 * Stufengewichte (Index 0 = Stufe 1) aus einem Gewerk-Eintrag der nhk_crafts_matrix, wie sie
 * QUALITY_FURNISHINGS liefert: {level, value}-Einträge, am Ende die Gewichtung des Gewerks.
 * REVA zeigt damit genau die Vorbelegung der AVM an, statt eine eigene fest einzubauen (#19511).
 * null, wenn der Eintrag keine Stufen enthält.
 */
export function weightsFromMatrixEntry(entry) {
    const levels = Array.isArray(entry) ? entry.filter((e) => e?.level >= 1 && e?.level <= 5) : [];
    if (levels.length === 0) return null;
    return levels.reduce((weights, {level, value}) => weights.with(level - 1, value ?? 0), Array(5).fill(0));
}

function areWeightsInvalid(weights) {
    if (weights == null) return false;
    const rowSum = weights.reduce((acc, v) => acc + (v || 0), 0);
    return rowSum > 0 && (rowSum < 0.99 || rowSum > 1.0);
}

/**
 * Order of the quality furnishings crafts as in ImmoWertV Anlage 4
 */
export const QUALITY_FURNISHINGS_ORDER = [
    "prop_craft_exterior_walls",
    "prop_craft_roofs",
    "prop_craft_exterior_doors_windows",
    "prop_craft_interior_walls_doors",
    "prop_craft_ceiling_stairs",
    "prop_craft_flooring",
    "prop_craft_sanitary",
    "prop_craft_heating",
    "prop_craft_technical_equipment",
];

/**
 * Beschreibung der Gebäudestandards je Standardstufe (1-5) nach ImmoWertV Anlage 4,
 * für den Hinweis-Tooltip beim Erfassen der Ausstattungsklassifikation
 */
const QUALITY_FURNISHINGS_DESCRIPTIONS = {
    prop_craft_exterior_walls: {
        1: "Holzfachwerk, Ziegelmauerwerk; Fugenglattstrich, Putz, Verkleidung mit Faserzementplatten, Bitumen-Schindeln oder einfachen Kunststoffplatten; kein oder deutlich nicht zeitgemäßer Wärmeschutz (vor ca. 1980)",
        2: "ein- /zweischaliges Mauerwerk, z. B. Gitterziegel oder Hohlblocksteine; verputzt und gestrichen oder Holzverkleidung; nicht zeitgemäßer Wärmeschutz (vor ca. 1995)",
        3: "ein- /zweischaliges Mauerwerk, z. B. aus Leichtziegeln, Kalksandsteinen, Gasbetonsteinen; Edelputz; Wärmedämmverbundsystem oder Wärmedämmputz (nach ca. 1995)",
        4: "Verblendmauerwerk, zweischalig, hinterlüftet, Vorhangfassade (z. B. Naturschiefer); Wärmedämmung (nach ca. 2005)",
        5: "aufwendig gestaltete Fassaden mit konstruktiver Gliederung (Säulenstellungen, Erker etc.), Sichtbeton-Fertigteile, Natursteinfassade, Elemente aus Kupfer-/Eloxalblech, mehrgeschossige Glasfassaden; Dämmung im Passivhausstandard",
    },
    prop_craft_roofs: {
        1: "Dachpappe, Faserzementplatten/Wellplatten; keine bis geringe Dachdämmung",
        2: "einfache Betondachsteine oder Tondachziegel, Bitumenschindeln; nicht zeitgemäße Dachdämmung (vor ca. 1995)",
        3: "Faserzement-Schindeln, beschichtete Betondachsteine und Tondachziegel, Folienabdichtung; Rinnen und Fallrohre aus Zinkblech; Dachdämmung (nach ca. 1995)",
        4: "glasierte Tondachziegel, Flachdachausbildung tlw. als Dachterrassen; Konstruktion in Brettschichtholz, schweres Massivflachdach; besondere Dachformen, z. B. Mansarden-, Walmdach; Aufsparrendämmung, überdurchschnittliche Dämmung (nach ca. 2005)",
        5: "hochwertige Eindeckung z. B. aus Schiefer oder Kupfer, Dachbegrünung, befahrbares Flachdach; aufwendig gegliederte Dachlandschaft, sichtbare Bogendachkonstruktionen; Rinnen und Fallrohre aus Kupfer; Dämmung im Passivhausstandard",
    },
    prop_craft_exterior_doors_windows: {
        1: "Einfachverglasung; einfache Holztüren",
        2: "Zweifachverglasung (vor ca. 1995); Haustür mit nicht zeitgemäßem Wärmeschutz (vor ca. 1995)",
        3: "Zweifachverglasung (nach ca. 1995), Rollläden (manuell); Haustür mit zeitgemäßem Wärmeschutz (nach ca. 1995)",
        4: "Dreifachverglasung, Sonnenschutzglas, aufwendigere Rahmen, Rollläden (elektr.); höherwertige Türanlage z. B. mit Seitenteil, besonderer Einbruchschutz",
        5: "große, feststehende Fensterflächen, Spezialverglasung (Schall- und Sonnenschutz); Außentüren in hochwertigen Materialien",
    },
    prop_craft_interior_walls_doors: {
        1: "Fachwerkwände, einfache Putze/Lehmputze, einfache Kalkanstriche; Füllungstüren, gestrichen, mit einfachen Beschlägen ohne Dichtungen",
        2: "massive tragende Innenwände, nicht tragende Wände in Leichtbauweise (z. B. Holzständerwände mit Gipskarton), Gipsdielen; leichte Türen, Stahlzargen",
        3: "nicht tragende Innenwände in massiver Ausführung bzw. mit Dämmmaterial gefüllte Ständerkonstruktionen; schwere Türen, Holzzargen",
        4: "Sichtmauerwerk, Wandvertäfelungen (Holzpaneele); Massivholztüren, Schiebetürelemente, Glastüren, strukturierte Türblätter",
        5: "gestaltete Wandabläufe (z. B. Pfeilervorlagen, abgesetzte oder geschwungene Wandpartien); Vertäfelungen (Edelholz, Metall), Akustikputz, Brandschutzverkleidung; raumhohe aufwendige Türelemente",
    },
    prop_craft_ceiling_stairs: {
        1: "Holzbalkendecken ohne Füllung, Spalierputz; Weichholztreppen in einfacher Art und Ausführung; kein Trittschallschutz",
        2: "Holzbalkendecken mit Füllung, Kappendecken; Stahl- oder Hartholztreppen in einfacher Art und Ausführung",
        3: "Beton- und Holzbalkendecken mit Tritt- und Luftschallschutz (z. B. schwimmender Estrich); geradläufige Treppen aus Stahlbeton oder Stahl, Harfentreppe, Trittschallschutz",
        4: "Decken mit größerer Spannweite, Deckenverkleidung (Holzpaneele/Kassetten); gewendelte Treppen aus Stahlbeton oder Stahl, Hartholztreppenanlage in besserer Art und Ausführung",
        5: "Decken mit großen Spannweiten, gegliedert, Deckenverkleidungen (Edelholz, Metall); breite Stahlbeton-, Metall- oder Hartholztreppenanlage mit hochwertigem Geländer",
    },
    prop_craft_flooring: {
        1: "ohne Belag",
        2: "Linoleum-, Teppich-, Laminat- und PVC-Böden einfacher Art und Ausführung",
        3: "Linoleum-, Teppich-, Laminat- und PVC-Böden besserer Art und Ausführung, Fliesen, Kunststoffplatten",
        4: "Natursteinplatten, Fertigparkett, hochwertige Fliesen, Terrazzobelag, hochwertige Massivholzböden auf gedämmter Unterkonstruktion",
        5: "hochwertiges Parkett, hochwertige Natursteinplatten, hochwertige Edelholzböden auf gedämmter Unterkonstruktion",
    },
    prop_craft_sanitary: {
        1: "einfaches Bad mit Stand-WC; Installation auf Putz; Ölfarbenanstrich, einfache PVC-Bodenbeläge",
        2: "1 Bad mit WC, Dusche oder Badewanne; einfache Wand- und Bodenfliesen, teilweise gefliest",
        3: "1 Bad mit WC, Dusche und Badewanne, Gäste-WC; Wand- und Bodenfliesen, raumhoch gefliest",
        4: "1 bis 2 Bäder mit tlw. zwei Waschbecken, tlw. Bidet/Urinal, Gäste-WC, bodengleiche Dusche; Wand- und Bodenfliesen; jeweils in gehobener Qualität",
        5: "mehrere großzügige, hochwertige Bäder, Gäste-WC; hochwertige Wand- und Bodenplatten (oberflächenstrukturiert, Einzel- und Flächendekors)",
    },
    prop_craft_heating: {
        1: "Einzelöfen, Schwerkraftheizung",
        2: "Fern- oder Zentralheizung, einfache Warmluftheizung, einzelne Gasaußenwandthermen, Nachtstromspeicher-, Fußbodenheizung (vor ca. 1995)",
        3: "elektronisch gesteuerte Fern- oder Zentralheizung, Niedertemperatur- oder Brennwertkessel",
        4: "Fußbodenheizung, Solarkollektoren für Warmwassererzeugung, zusätzlicher Kaminanschluss",
        5: "Solarkollektoren für Warmwassererzeugung und Heizung, Blockheizkraftwerk, Wärmepumpe, Hybrid-Systeme; aufwendige zusätzliche Kaminanlage",
    },
    prop_craft_technical_equipment: {
        1: "sehr wenige Steckdosen, Schalter und Sicherungen, kein Fehlerstromschutzschalter (FI-Schalter), Leitungen teilweise auf Putz",
        2: "wenige Steckdosen, Schalter und Sicherungen",
        3: "zeitgemäße Anzahl an Steckdosen und Lichtauslässen, Zählerschrank (ab ca. 1985) mit Unterverteilung und Kippsicherungen",
        4: "zahlreiche Steckdosen und Lichtauslässe, hochwertige Abdeckungen, dezentrale Lüftung mit Wärmetauscher, mehrere LAN- und Fernsehanschlüsse",
        5: "Video- und zentrale Alarmanlage, zentrale Lüftung mit Wärmetauscher, Klimaanlage, Bussystem",
    },
};

export function ObjectInputQualityFurnishings() {
    const {t, language} = useRevaTranslation();
    const dispatch = useDispatch();
    const store = useRevaStore();
    const spec = useSelector(selectSpec);
    const segment = useSelector(selectSegment);
    const initialised = useSelector(selectPersistenceInitialized);
    const readOnly = useSelector(selectReadOnly);
    const inspectionDerived = useSelector(selectAllInspectionDerivedParams);
    const inspectionParams = useSelector(selectAllInspectionParams);
    const {parameterValues, onValuesChange} = useMultipleParameterValues(OUTPUT_PARAMS, "INSPECTION_DERIVED");
    const nhkCraftsMatrix = parameterValues["nhk_crafts_matrix"]?.activeParameterValue;
    const inputParams = spec?.apiParams?.QUALITY_FURNISHINGS?.[segment] || {};
    const crafts = Object.keys(inputParams);
    const canReset = !readOnly && crafts.some((craft) => inspectionDerived?.[craft] != null);

    const showPreview = async () => {
        const preview = await dispatch(queryQualityFurnishingsAsync(crafts));
        if (preview) dispatch(setQualityFurnishingsPreview(preview));
    };

    // Aus dem Store statt aus inspectionDerived: der Handler der Zelle stammt ggf. aus einem Render vor dem letzten Speichern.
    const hasOwnValue = (param) => selectAllInspectionDerivedParams(store.getState())?.[param] != null;

    // Geprüft wird jede Zeile, nicht nur die bearbeitete: die Abfrage schickt alle mit, und die AVM normalisiert
    // eine ungültige Zeile still, statt sie abzulehnen. Quelle wie beim Senden: eigene Gewichte, sonst Besichtigung.
    const anyRowInvalid = () => {
        const state = store.getState();
        const derived = selectAllInspectionDerivedParams(state);
        const inspection = selectAllInspectionParams(state);
        return crafts.some((craft) => areWeightsInvalid(derived?.[craft] ?? inspection?.[craft]));
    };

    const clearOwnValues = (params) => {
        const stored = params.filter(hasOwnValue);
        if (stored.length > 0) onValuesChange(Object.fromEntries(stored.map((param) => [param, null])));
    };

    // #19507: Werte aus Aufruf beim Öffnen nur als preview anzeigen, nicht nach INSPECTION_DERIVED speichern.
    useEffect(() => {
        if (!readOnly && initialised && nhkCraftsMatrix == null && segment !== SEGMENT.WHG_K) {
            showPreview();
        }
    }, [initialised]);

    const resetToPreview = (params) => {
        abortQualityFurnishingsQuery();
        dispatch(setQualityFurnishingsPreview(null));
        clearOwnValues(params);
        if (inspectionParams?.nhk_crafts_matrix == null) showPreview();
    };

    const fetchQualityFurnishings = async () => {
        if (readOnly) return;
        if (!crafts.some(hasOwnValue)) {
            resetToPreview(RESULT_PARAMS);
            return;
        }

        if (anyRowInvalid()) {
            abortQualityFurnishingsQuery();
            dispatch(setQualityFurnishingsPreview(null));
            clearOwnValues(["nhk_crafts_based_furnishings", "quality_furnishings_continuous"]);
            return;
        }

        dispatch(setQualityFurnishingsPreview(null));
        const outputs = await dispatch(queryQualityFurnishingsAsync(crafts));
        if (!outputs) return;
        onValuesChange({...outputs, quality_furnishings_continuous: outputs.nhk_crafts_based_furnishings});
    };

    const rows = crafts
        .map((g) => {
            const param = g.replace("_weights", "");
            return {
                craft: g,
                param,
                label: craftLabel(inputParams[g], language),
                description: selectByLanguage(language, inputParams[g]?.descriptionDe, inputParams[g]?.descriptionEn),
            };
        })
        .sort((a, b) => QUALITY_FURNISHINGS_ORDER.indexOf(a.param) - QUALITY_FURNISHINGS_ORDER.indexOf(b.param));

    const fields = segment === SEGMENT.MFHWGH_K ? [2, 3, 4] : [0, 1, 2, 3, 4];
    const levelLabels = segment === SEGMENT.MFHWGH_K ? ["3", "4", "5"] : ["1", "2", "3", "4", "5"];

    const handleReset = () => resetToPreview([...RESULT_PARAMS, ...crafts]);

    const firstColumnHeader = (
        <span className="flex items-center gap-1">
            <span className="p-column-title">{t("object.tab_inspection.input.col_description")}</span>
            {canReset && <ResetIcon parameter="quality_furnishings" onValueChange={handleReset} />}
        </span>
    );

    const headerGroup = (
        <ColumnGroup>
            <Row>
                <Column header={firstColumnHeader} colSpan={1} />
                <Column
                    header={t("object.tab_inspection.output.quality_furnishings_table.col_standard")}
                    colSpan={fields.length}
                    headerClassName={"p-align-center"}
                />
            </Row>
            <Row>
                <Column header="" />
                {levelLabels.map((label) => (
                    <Column key={label} header={label} headerClassName={"p-align-right w-24"} />
                ))}
            </Row>
        </ColumnGroup>
    );

    if (segment === SEGMENT.WHG_K) return null;

    return (
        <>
            <Tooltip
                target=".quality-furnishings-cell, .quality-furnishings-label-cell"
                position="right"
                className="custom-tooltip nhk-tooltip"
            />
            <DataTable
                value={rows}
                size="small"
                headerColumnGroup={headerGroup}
                footer={<div />}
                className="p-inputtext-sm text-sm inputtext-compact"
            >
                <Column
                    field="label"
                    body={(row) => (
                        <span className="quality-furnishings-label-cell" data-pr-tooltip={row.description}>
                            {row.label}
                        </span>
                    )}
                />
                {fields.map((field) => (
                    <Column
                        key={field}
                        body={(row) => (
                            <CellInputWeight
                                craft={row.craft}
                                field={field}
                                firstField={fields[0]}
                                segment={segment}
                                language={language}
                                onAfterChange={fetchQualityFurnishings}
                                disabled={readOnly}
                            />
                        )}
                    />
                ))}
            </DataTable>
        </>
    );
}

function CellInputWeight({craft, field, firstField, segment, language, onAfterChange, disabled}) {
    const {parameterValues, onValueChange} = useParameterValue(craft, "INSPECTION_DERIVED");
    // Vorbelegung als Platzhalter aus der Matrix der AVM (gespeichert, Besichtigung oder Vorschau), nie ein eigener Default (#19511)
    const {parameterValues: matrixValues} = useParameterValue("nhk_crafts_matrix", "INSPECTION_DERIVED");
    const preview = useSelector(selectQualityFurnishingsPreview);
    const matrixKey = useSelector(selectQualityFurnishingsCraftToMatrixKeyCurried(segment))?.[
        craft.replace("_weights", "")
    ];
    const matrix = matrixValues?.activeParameterValue ?? preview?.nhk_crafts_matrix;
    const suggestedWeights = weightsFromMatrixEntry(matrix?.[matrixKey]);
    const rowValues = foldQualityFurnishingsWeights(
        parameterValues?.activeParameterValue ?? Array(5).fill(0),
        firstField,
    );
    const currentValue = rowValues[field] || null;
    const suggestedValue = suggestedWeights ? foldQualityFurnishingsWeights(suggestedWeights, firstField)[field] : 0;
    const description = QUALITY_FURNISHINGS_DESCRIPTIONS[craft.replace("_weights", "")]?.[field + 1];

    return (
        <div className="quality-furnishings-cell" data-pr-tooltip={description}>
            <InputNumber
                value={currentValue}
                minFractionDigits={2}
                maxFractionDigits={2}
                inputClassName="text-right w-full"
                onValueChange={(e) => {
                    if (e.originalEvent && e.value !== currentValue) {
                        let newRowValues = [...rowValues].with(field, e.value ?? 0);
                        newRowValues = newRowValues.find((v) => v != null && v > 0 && v <= 1) ? newRowValues : null;
                        onValueChange(newRowValues);
                        onAfterChange();
                    }
                }}
                locale={language}
                max={1}
                min={0}
                invalid={areWeightsInvalid(rowValues)}
                placeholder={
                    parameterValues?.activeParameterValue == null && suggestedValue > 0
                        ? suggestedValue.toLocaleString(language)
                        : ""
                }
                disabled={disabled}
            />
        </div>
    );
}
